import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import Breadcrumb from "components/Common/Breadcrumb"
import DashboardLoader from "components/Loader/DashboardLoader"

// API Service for Dashboard
const dashboardApiService = {
  // Get all customers
  getAllCustomers: async () => {
    try {
      const response = await fetch("https://qwikbistro.shop/userProfile/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      })
      return await response.json()
    } catch (error) {
      console.error("Error fetching customers:", error)
      throw error
    }
  },

  // Get all orders
  getAllOrders: async () => {
    try {
      const response = await fetch("https://qwikbistro.shop/order/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      })
      return await response.json()
    } catch (error) {
      console.error("Error fetching orders:", error)
      throw error
    }
  },

  // Get all expenses
  getAllExpenses: async () => {
    try {
      const response = await fetch("https://qwikbistro.shop/expenses/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      })
      return await response.json()
    } catch (error) {
      console.error("Error fetching expenses:", error)
      throw error
    }
  },

  // Get all menu items
  getMenuItems: async () => {
    try {
      const response = await fetch("https://qwikbistro.shop/menuItem/", {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      })
      return await response.json()
    } catch (error) {
      console.error("Error fetching menu items:", error)
      throw error
    }
  },
}

const QwikBistroDashboard = props => {
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState({
    customerCount: 0,
    orderCount: 0,
    totalEarnings: 0,
    totalExpenses: 0,
    totalProfit: 0,
    menuItemCount: 0,
    allOrders: [],
  })
  const [analyticsData, setAnalyticsData] = useState({
    monthlySales: [],
    menuItemCounts: {},
    topCustomers: [],
  })
  const [refreshCounter, setRefreshCounter] = useState(0)

  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true)
      try {
        // Fetch all data in parallel
        const [customers, orders, expenses, menuItems] = await Promise.all([
          dashboardApiService.getAllCustomers(),
          dashboardApiService.getAllOrders(),
          dashboardApiService.getAllExpenses(),
          dashboardApiService.getMenuItems(),
        ])

        // Calculate metrics
        const customerCount = customers.filter(
          user => user.role === "customer"
        ).length
        const orderCount = orders.length
        const totalEarnings = orders.reduce(
          (sum, order) => sum + parseFloat(order.total_amount || 0),
          0
        )
        const totalExpenses = expenses.reduce(
          (sum, expense) => sum + parseFloat(expense.total || 0),
          0
        )
        const totalProfit = totalEarnings - totalExpenses
        const menuItemCount = menuItems.length

        // Set metrics
        setMetrics({
          customerCount,
          orderCount,
          totalEarnings,
          totalExpenses,
          totalProfit,
          menuItemCount,
          allOrders: orders,
        })

        // Calculate analytics data
        const analytics = calculateAnalyticsData(orders, customers)
        setAnalyticsData(analytics)
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
        toast.error("Failed to load dashboard data")
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [refreshCounter])

  const calculateAnalyticsData = (orders, customers) => {
    // Calculate last 6 months sales
    const currentDate = new Date()
    const monthlySales = []

    for (let i = 0; i < 6; i++) {
      const monthDate = new Date(currentDate)
      monthDate.setMonth(monthDate.getMonth() - i)

      const monthName = monthDate.toLocaleString("default", { month: "short" })
      const year = monthDate.getFullYear()
      const monthKey = `${monthName} ${year}`

      const startOfMonth = new Date(year, monthDate.getMonth(), 1)
      const endOfMonth = new Date(year, monthDate.getMonth() + 1, 0)

      const monthlyOrders = orders.filter(order => {
        const orderDate = new Date(order.order_date)
        return orderDate >= startOfMonth && orderDate <= endOfMonth
      })

      const totalSales = monthlyOrders.reduce(
        (sum, order) => sum + parseFloat(order.total_amount || 0),
        0
      )

      monthlySales.push({
        month: monthKey,
        sales: totalSales,
        orderCount: monthlyOrders.length,
      })
    }

    // Calculate menu item counts
    const menuItemCounts = {}
    orders.forEach(order => {
      order?.orderproductdata?.forEach(item => {
        menuItemCounts[item.name] =
          (menuItemCounts[item.name] || 0) + (item.quantity || 1)
      })
    })

    // Calculate top customers
    const customerStats = {}
    orders.forEach(order => {
      const customerId = order.user_id
      if (!customerStats[customerId]) {
        customerStats[customerId] = {
          orderCount: 0,
          totalSpent: 0,
          customer: customers.find(c => c.id == customerId),
        }
      }
      customerStats[customerId].orderCount += 1
      customerStats[customerId].totalSpent += parseFloat(
        order.total_amount || 0
      )
    })

    const topCustomers = Object.values(customerStats)
      .filter(c => c.customer)
      .sort(
        (a, b) => b.orderCount - a.orderCount || b.totalSpent - a.totalSpent
      )
      .slice(0, 10)

    return {
      monthlySales,
      menuItemCounts,
      topCustomers,
    }
  }

  const formatCurrency = amount => {
    return `$${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1)
    toast.success("Dashboard data refreshed")
  }

  if (loading) {
    return <DashboardLoader />
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Dashboard" breadcrumbItem="Analytics Overview" />

        {/* Metrics Cards */}
        <div className="row">
          {/* Total Customers */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                      <i className="fas fa-users"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Total Customers</p>
                    <h4 className="mb-0">{metrics.customerCount}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Orders */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <i className="fas fa-shopping-cart"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Total Orders</p>
                    <h4 className="mb-0">{metrics.orderCount}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                      <i className="fas fa-utensils"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Menu Items</p>
                    <h4 className="mb-0">{metrics.menuItemCount}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Earnings */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                      <i className="fas fa-chart-line"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Total Sales</p>
                    <h4 className="mb-0">
                      {formatCurrency(metrics.totalEarnings)}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Expenses */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                      <i className="fas fa-money-bill-wave"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Total Expenses</p>
                    <h4 className="mb-0">
                      {formatCurrency(metrics.totalExpenses)}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total Profit */}
          <div className="col-xl-2 col-md-4 col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                      <i className="fas fa-hand-holding-usd"></i>
                    </span>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-1">Total Profit</p>
                    <h4 className="mb-0">
                      {formatCurrency(metrics.totalProfit)}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="row mt-4">
          {/* Last 6 Months Sales */}
          <div className="col-xl-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Sales (Last 6 Months)</h5>
                  {/* <Link to="/orders" className="text-primary">
                    View All <i className="fas fa-arrow-right ms-1"></i>
                  </Link> */}
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <table className="table table-hover table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Sales</th>
                        {/* <th className="text-end">Orders</th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.monthlySales.map(monthData => (
                        <tr key={monthData.month}>
                          <td
                            style={{
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            }}
                          >
                            <span className="">{monthData.month}</span>
                          </td>
                          <td
                            className="text-end"
                            style={{
                              paddingTop: "10px",
                              paddingBottom: "10px",
                            }}
                          >
                            <span className="">
                              {formatCurrency(monthData.sales)}
                            </span>
                          </td>
                          {/* <td className="text-end">
                            <span className="badge bg-primary">
                              {monthData.orderCount}
                            </span>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Most Ordered Menu Items */}
          <div className="col-xl-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Most Ordered Items</h5>
                  {/* <Link to="/menu-items" className="text-primary">
                    View All <i className="fas fa-arrow-right ms-1"></i>
                  </Link> */}
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <table className="table table-hover table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Menu Item</th>
                        <th className="text-center">Orders</th>
                        <th className="text-center">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(analyticsData.menuItemCounts)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 10)
                        .map(([name, count]) => {
                          // Find item image and order count
                          let itemImage = null
                          let timesOrdered = 0

                          // Find the first occurrence for image
                          const allOrders = analyticsData.monthlySales.reduce(
                            (acc, month) => {
                              // This would need the full orders data to work properly
                              return acc
                            },
                            []
                          )

                          // Calculate order count (simplified)
                          timesOrdered = Math.ceil(count / 3) // Simplified calculation

                          // Count how many orders contain this item
                          metrics?.allOrders?.forEach(order => {
                            const foundItem = order.orderproductdata.find(
                              item => item.name === name
                            )
                            if (foundItem) {
                              timesOrdered++
                              if (!itemImage) {
                                itemImage = foundItem.image
                              }
                            }
                          })

                          return (
                            <tr key={name}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {itemImage ? (
                                    <img
                                      src={itemImage}
                                      alt={name}
                                      className="avatar-sm rounded me-2"
                                      style={{ width: "40px", height: "40px" }}
                                    />
                                  ) : (
                                    <div className="avatar-sm bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                      <i className="fas fa-utensils text-muted"></i>
                                    </div>
                                  )}
                                  <span
                                    className="text-truncate"
                                    style={{ maxWidth: "150px" }}
                                  >
                                    {name}
                                  </span>
                                </div>
                              </td>
                              <td className="text-center">
                                {timesOrdered}
                                {/* <span className="badge bg-info">
                                </span> */}
                              </td>
                              <td className="text-center">
                                {count}
                                {/* <span className="text-center">
                                  </span> */}
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Top Customers */}
          <div className="col-xl-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">Top Customers</h5>
                  {/* <Link to="/customers" className="text-primary">
                    View All <i className="fas fa-arrow-right ms-1"></i>
                  </Link> */}
                </div>
                <div
                  className="table-responsive"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <table className="table table-hover table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th className="text-center">Orders</th>
                        <th className="text-end">Spent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.topCustomers.map((customerStat, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              {customerStat.customer?.photo ? (
                                <img
                                  src={customerStat.customer.photo}
                                  alt={customerStat.customer.name}
                                  className="avatar-sm rounded-circle me-2"
                                  style={{ width: "40px", height: "40px" }}
                                />
                              ) : (
                                <div
                                  className="avatar-sm bg-light rounded-circle me-2 d-flex align-items-center justify-content-center"
                                  style={{ width: "40px", height: "40px" }}
                                >
                                  <i className="fas fa-user text-muted"></i>
                                </div>
                              )}
                              <div>
                                <h6
                                  className="mb-0 text-truncate"
                                  style={{ maxWidth: "120px" }}
                                >
                                  {customerStat.customer?.name ||
                                    "Unknown Customer"}
                                </h6>
                                {/* <small className="text-muted">
                                  {customerStat.customer?.email || "No email"}
                                </small> */}
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            {customerStat.orderCount}
                            {/* <span className="badge bg-primary">
                            </span> */}
                          </td>
                          <td className="text-end">
                            <span className="">
                              {formatCurrency(customerStat.totalSpent)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        {/* <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">Quick Actions</h5>
                <div className="row">
                  <div className="col-md-3 col-sm-6 mb-3">
                    <Link
                      to="/orders/create"
                      className="card card-hover text-center h-100"
                    >
                      <div className="card-body">
                        <div className="avatar-sm mx-auto mb-3">
                          <span className="avatar-title bg-success-subtle text-success rounded-circle fs-2">
                            <i className="fas fa-plus"></i>
                          </span>
                        </div>
                        <h6 className="mb-1">Create Order</h6>
                        <p className="text-muted mb-0">New customer order</p>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-3">
                    <Link
                      to="/customers"
                      className="card card-hover text-center h-100"
                    >
                      <div className="card-body">
                        <div className="avatar-sm mx-auto mb-3">
                          <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-2">
                            <i className="fas fa-user-plus"></i>
                          </span>
                        </div>
                        <h6 className="mb-1">Manage Customers</h6>
                        <p className="text-muted mb-0">View all customers</p>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-3">
                    <Link
                      to="/menu-items"
                      className="card card-hover text-center h-100"
                    >
                      <div className="card-body">
                        <div className="avatar-sm mx-auto mb-3">
                          <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-2">
                            <i className="fas fa-utensils"></i>
                          </span>
                        </div>
                        <h6 className="mb-1">Menu Items</h6>
                        <p className="text-muted mb-0">Manage menu</p>
                      </div>
                    </Link>
                  </div>

                  <div className="col-md-3 col-sm-6 mb-3">
                    <Link
                      to="/reports"
                      className="card card-hover text-center h-100"
                    >
                      <div className="card-body">
                        <div className="avatar-sm mx-auto mb-3">
                          <span className="avatar-title bg-info-subtle text-info rounded-circle fs-2">
                            <i className="fas fa-chart-bar"></i>
                          </span>
                        </div>
                        <h6 className="mb-1">Reports</h6>
                        <p className="text-muted mb-0">View detailed reports</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  )
}

// QwikBistroDashboard.propTypes = {
//   t: PropTypes.any,
// }

export default QwikBistroDashboard
